/**
 * Reusable Analytics Dashboard Components
 * Display student progress, achievements, and statistics
 */

import React from 'react';

/**
 * Progress Card Component
 * Displays a single metric with icon, label, and value
 */
export const ProgressCard = ({ icon, label, value, subtext, color = '#27b05b' }) => {
    const styles = {
        card: {
            background: `linear-gradient(135deg, ${color}15, ${color}08)`,
            border: `2px solid ${color}30`,
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            transition: 'transform 0.2s, box-shadow 0.2s'
        },
        icon: {
            fontSize: '2.5rem',
            marginBottom: '10px'
        },
        value: {
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#1e293b',
            margin: '10px 0'
        },
        label: {
            fontSize: '0.9rem',
            color: '#64748b',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        subtext: {
            fontSize: '0.8rem',
            color: '#94a3b8',
            marginTop: '8px'
        }
    };

    return (
        <div style={styles.card} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
            <div style={styles.icon}>{icon}</div>
            <div style={styles.label}>{label}</div>
            <div style={styles.value}>{value}</div>
            {subtext && <div style={styles.subtext}>{subtext}</div>}
        </div>
    );
};

/**
 * Achievement Badge Component
 */
export const AchievementBadge = ({ name, description, earned = false, icon = '🏆' }) => {
    const styles = {
        container: {
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            padding: '15px',
            borderRadius: '12px',
            background: earned ? 'linear-gradient(135deg, #fef9c3, #fef3c7)' : '#f3f4f6',
            border: earned ? '2px solid #f59e0b' : '2px dashed #cbd5e1',
            opacity: earned ? 1 : 0.6,
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        icon: {
            fontSize: earned ? '2.5rem' : '2rem',
            filter: earned ? 'drop-shadow(0 2px 4px rgba(245,158,11,0.3))' : 'grayscale(100%)'
        },
        name: {
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#1e293b'
        },
        description: {
            fontSize: '0.8rem',
            color: '#64748b',
            marginTop: '4px'
        }
    };

    return (
        <div style={styles.container} title={description}>
            <div style={styles.icon}>{icon}</div>
            <div style={styles.name}>{name}</div>
            {earned && <div style={styles.description}>{description}</div>}
        </div>
    );
};

/**
 * Learning Streak Counter
 */
export const StreakCounter = ({ days = 0, maxDays = 30 }) => {
    const percentage = (days / maxDays) * 100;

    const styles = {
        container: {
            padding: '20px',
            background: 'linear-gradient(135deg, #ff612615, #ff612610)',
            borderRadius: '12px',
            border: '2px solid #ff612630'
        },
        progressBar: {
            width: '100%',
            height: '40px',
            background: '#f3f4f6',
            borderRadius: '20px',
            overflow: 'hidden',
            marginBottom: '15px'
        },
        progress: {
            height: '100%',
            width: `${Math.min(percentage, 100)}%`,
            background: 'linear-gradient(90deg, #ff6126, #ff9500)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: '15px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            transition: 'width 0.3s ease'
        },
        text: {
            textAlign: 'center'
        },
        number: {
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: '#ff6126'
        },
        label: {
            fontSize: '0.9rem',
            color: '#64748b',
            marginTop: '5px'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.progressBar}>
                <div style={styles.progress}>
                    {days > 0 && `${days} days`}
                </div>
            </div>
            <div style={styles.text}>
                <div style={styles.number}>🔥 {days}</div>
                <div style={styles.label}>Day Learning Streak</div>
            </div>
        </div>
    );
};

/**
 * Stats Grid Component
 */
export const StatsGrid = ({ stats = [] }) => {
    const styles = {
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginTop: '20px'
        }
    };

    return (
        <div style={styles.grid}>
            {stats.map((stat, idx) => (
                <ProgressCard key={idx} {...stat} />
            ))}
        </div>
    );
};

export default {
    ProgressCard,
    ProgressCard,
    AchievementBadge,
    StreakCounter,
    StatsGrid
};
